import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Container,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import {
  getFlags,
  updateFlagStatus,
  approveCreditRequest,
  takeDownImage,
} from '../firebase/admin';
import CreditDisplay from '../components/media/CreditDisplay';

const AdminFlags = () => {
  const [pendingFlags, setPendingFlags] = useState([]);
  const [resolvedFlags, setResolvedFlags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  const fetchFlags = useCallback(async () => {
    setIsLoading(true);
    const pendingResult = await getFlags('pending');
    if (pendingResult.success) {
      setPendingFlags(pendingResult.flags);
    } else {
      toast({ title: 'Error fetching pending flags', description: pendingResult.error, status: 'error' });
    }

    const resolvedResult = await getFlags('resolved');
    if (resolvedResult.success) {
      setResolvedFlags(resolvedResult.flags);
    } else {
      toast({ title: 'Error fetching resolved flags', description: resolvedResult.error, status: 'error' });
    }
    setIsLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const handleApproveCredit = async (flag) => {
    const { success, error } = await approveCreditRequest(flag);
    if (success) {
      toast({ title: 'Credit approved', status: 'success' });
      fetchFlags();
    } else {
      toast({ title: 'Error approving credit', description: error, status: 'error' });
    }
  };

  const handleTakeDown = async (flag) => {
    const { success, error } = await takeDownImage(flag);
    if (success) {
      toast({ title: 'Image taken down', status: 'success' });
      fetchFlags();
    } else {
      toast({ title: 'Error taking down image', description: error, status: 'error' });
    }
  };

  const handleDismiss = async (flagId) => {
    const { success, error } = await updateFlagStatus(flagId, 'dismissed');
    if (success) {
      toast({ title: 'Flag dismissed', status: 'info' });
      fetchFlags();
    } else {
      toast({ title: 'Error dismissing flag', description: error, status: 'error' });
    }
  };

  const renderFlagRow = (flag) => (
    <Tr key={flag.id}>
      <Td>{flag.type}</Td>
      <Td>{flag.mediaId}</Td>
      <Td>
        {flag.type === 'credit' ? (
          <CreditDisplay credits={flag.details.creditData} />
        ) : (
          <Text>{flag.details.reason}</Text>
        )}
      </Td>
      <Td>
        <HStack>
          {flag.status === 'pending' && (
            <>
              {flag.type === 'credit' && (
                <Button size="sm" colorScheme="green" onClick={() => handleApproveCredit(flag)}>
                  Approve Credit
                </Button>
              )}
              {flag.type === 'dmca' && (
                <Button size="sm" colorScheme="red" onClick={() => handleTakeDown(flag)}>
                  Take Down
                </Button>
              )}
              <Button size="sm" onClick={() => handleDismiss(flag.id)}>
                Dismiss
              </Button>
            </>
          )}
        </HStack>
      </Td>
    </Tr>
  );

  return (
    <Container maxW="container.xl" py={10}>
      <VStack spacing={8} align="stretch">
        <Heading>Manage Image Flags</Heading>
        {isLoading ? (
          <Text>Loading flags...</Text>
        ) : (
          <VStack align="stretch" spacing={8}>
            <Box>
              <Heading size="md" mb={4}>Pending Flags</Heading>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Type</Th>
                    <Th>Media ID</Th>
                    <Th>Details</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>{pendingFlags.map(renderFlagRow)}</Tbody>
              </Table>
            </Box>
            <Accordion allowToggle>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Resolved Flags
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Type</Th>
                        <Th>Media ID</Th>
                        <Th>Details</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {resolvedFlags.map((flag) => (
                        <Tr key={flag.id}>
                          <Td>{flag.type}</Td>
                          <Td>{flag.mediaId}</Td>
                          <Td>
                            {flag.type === 'credit' ? (
                              <CreditDisplay credits={flag.details.creditData} />
                            ) : (
                              <Text>{flag.details.reason}</Text>
                            )}
                          </Td>
                          <Td>{flag.status}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </VStack>
        )}
      </VStack>
    </Container>
  );
};

export default AdminFlags;
